import { gql } from '@apollo/client';
import { Box, chakra, Flex, HStack, Tag, Text } from '@chakra-ui/react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '~components/ui/BackButton';
import { SiteIcon } from '~components/ui/icons';
import { PageHeading } from '~components/ui/PageHeading';
import { ConfigurationStatus } from '~graphql/__generated__/types';
import { ConfigurationStatusBox } from '../ConfigurationStatus';
import { ConfigDetailHeader_ConfigFragment } from './__generated__/ConfigDetailHeader.graphql';

export default function ConfigDetailHeader({
  config,
  title,
  initialState,
}: {
  config: ConfigDetailHeader_ConfigFragment;
  title: string;
  initialState?: ConfigurationStatus;
}) {
  const navigate = useNavigate();
  const handleGoBack = useCallback(() => {
    navigate('../configurations');
  }, [navigate]);

  return (
    <PageHeading
      floatingButton={<BackButton onClick={handleGoBack} />}
      labels={
        <>
          <Flex justifyContent={'space-between'} width={'100%'}>
            <HStack gap={4}>
              {config.site && (
                <HStack spacing={2}>
                  <SiteIcon width="4" height="4" />
                  <chakra.span>{config.site.name}</chakra.span>
                </HStack>
              )}
              <Flex direction="row" flexWrap="wrap">
                {config.groups.map((group) => (
                  <Tag key={group.id} variant="outline" size="sm" mr="2">
                    {group.name}
                  </Tag>
                ))}
              </Flex>
            </HStack>
            {config.state && (
              <Box width={'130px'}>
                <ConfigurationStatusBox state={initialState || config.state} />
              </Box>
            )}
          </Flex>
        </>
      }
    >
      <Text
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        maxWidth={{
          base: '200px',
          sm: '300px',
          md: '400px',
          lg: '500px',
          xl: '600px',
        }}
        title={title}
      >
        {title}
      </Text>
    </PageHeading>
  );
}

ConfigDetailHeader.graphql = {
  fragments: {
    ConfigDetailHeader_config: gql`
      fragment ConfigDetailHeader_config on ConfigurationModel {
        id
        site {
          name
        }
        groups {
          id
          name
        }
        state
      }
    `,
  },
};
